import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { isEmpty, get, throttle } from 'lodash';

import Button from 'widgets/Button/Button';
import { scrollToSelector } from '../../utils/createReview';

import { wa } from 'apps/analytics';
import { pageEventFields, pageEvents } from '../../analytics';

const links = [
  {
    name: 'overview',
    label: 'Overview',
  },
  {
    name: 'productInformation',
    label: 'Information',
  },
  {
    name: 'categories',
    label: 'Categories',
    requiredProductDetails: 'backOfPack.tags',
  },
  {
    name: 'reviews',
    label: 'Reviews',
  },
  {
    name: 'recipes',
    label: 'Recipes',
    requiredProductDetails: 'recipes',
  },
];

export default class StickyNavigation extends PureComponent {
  static propTypes = {
    betaFeatures: PropTypes.array.isRequired,
    isInBasket: PropTypes.bool,
    productDetails: PropTypes.object,
    showRecommendations: PropTypes.bool,
  };

  state = {
    activeLink: null,
    renderClientSide: false,
  };

  componentDidMount() {
    window.addEventListener('scroll', this.updateActiveLink);
    this.setState({ renderClientSide: true });
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.updateActiveLink);
  }

  getSectionCheckpoints = () => {
    const { pageYOffset } = window;
    const checkpoints = links.filter(link => (
      this.checkBetaFeature(link) &&
      this.checkSectionHasContents(link) &&
      this.checkSectionVisible(link)
    )).map(link => {
      const sectionRect = document.getElementById(link.name).getBoundingClientRect();
      return {
        name: link.name,
        start: pageYOffset + sectionRect.top,
      };
    });

    return checkpoints;
  };

  updateActiveLink = throttle(() => {
    const { pageYOffset } = window;
    const checkpoints = this.getSectionCheckpoints();
    const headerNode = document.querySelector('.rs-stickyHeader');
    const stickyTrolleyNode = document.querySelector('.bop-basketControls__sticky--isVisible');

    if (!headerNode || !stickyTrolleyNode) {
      return;
    }
    const headerHeight = headerNode.clientHeight || 0;
    const stickyTrolleyHeight = stickyTrolleyNode.clientHeight || 0;
    const checkpoint = pageYOffset + headerHeight + stickyTrolleyHeight + 36;
    const activeLink = checkpoints.reduceRight((prevCheckpoint, sectionCheckpoint) => {
      if (
        (checkpoint < prevCheckpoint.start) &&
        (checkpoint > sectionCheckpoint.start)
      ) {
        return sectionCheckpoint;
      } else {
        return prevCheckpoint;
      }
    }, { start: Infinity, name: '' }).name;

    this.setState({ activeLink });
  }, 50, { leading: true });

  checkBetaFeature = (link) => {
    const { betaFeatures } = this.props;

    if (!link.requiredBetaFeature) {
      return true;
    }

    return betaFeatures.includes(link.requiredBetaFeature);
  };

  checkSectionHasContents = (link) => {
    const { productDetails } = this.props;

    if (!link.requiredProductDetails) {
      return true;
    }

    const linkProductDetails = get(productDetails, link.requiredProductDetails, null);
    return (linkProductDetails && !isEmpty(linkProductDetails));
  };

  checkSectionVisible = (link) => {
    // prevent errors on ssr
    if (!global.document) {
      return false;
    }

    const section = document.getElementById(link.name);
    if (section) {
      return section.getBoundingClientRect().height > 0;
    }
    return false;
  };

  renderLink = (link) => {
    if (
      !this.checkBetaFeature(link) ||
      !this.checkSectionHasContents(link) ||
      !this.checkSectionVisible(link)
    ) {
      return null;
    }

    const { activeLink } = this.state;

    return (
      <li
        key={link.name}
        className={classNames(
          'bop-stickyNavigation__item',
          `bop-stickyNavigation__item--${link.name}`,
          { 'bop-stickyNavigation__item--active': link.name === activeLink }
        )}
      >
        <a
          href={`#${link.name}`}
          className="bop-stickyNavigation__link"
          onClick={this.handleLinkClick(`#${link.name}`, link.label)}
        >
          {link.label}
        </a>
      </li>
    );
  };

  handleLinkClick = (selector, label) => (event) => {
    wa.triggerPageEventWithDescription(
      pageEvents.BOP_STICKY_LINK_CLICKED,
      { [pageEventFields.STICKY_LINK_LABEL]: label }
    );
    scrollToSelector(selector, event);
  };

  handleRecommendedClicked = (event) => {
    wa.triggerPageEventWithDescription(pageEvents.BOP_STICKY_PRODUCTS_TO_BUY_CLICKED);
    scrollToSelector('#recommendationsScrollId', event);
  };

  render() {
    const { isInBasket, showRecommendations } = this.props;
    const { renderClientSide } = this.state;
    const { productDetails: { recommendations } = {} } = this.props;
    const hasRecommendations = get(recommendations, 'sections[0].fops', []).length > 0;

    return (
      <>
        {renderClientSide && (
          <ul className="bop-stickyNavigation">
            {links.map(link => this.renderLink(link))}
          </ul>
        )}
        {hasRecommendations && showRecommendations && (
          <Button
            secondary
            size="small"
            className={classNames(
              'bop-stickyNavigation__scrollToYML',
              { 'bop-stickyNavigation__scrollToYML--isVisible': isInBasket }
            )}
            onClick={this.handleRecommendedClicked}
          >
            Products to buy next
          </Button>
        )}
      </>
    );
  }
}
